import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  viewChild,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

const EASE_OUT_CUBIC_POWER = 3;

@Component({
  selector: 'fr8bot-web-statistics',
  templateUrl: './web-statistics.html',
  styleUrl: './web-statistics.scss',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebStatistics implements OnDestroy {
  private readonly statisticsSection =
    viewChild.required<ElementRef<HTMLElement>>('statisticsSection');
  private readonly counters = viewChildren<ElementRef<HTMLElement>>('counter');

  private observer?: IntersectionObserver;
  private animationFrameIds = new Map<HTMLElement, number>();
  private hasAnimated = false;

  constructor() {
    afterNextRender(() => this.observeSection());
  }

  private observeSection(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        const sectionEntry = entries[0];

        if (!sectionEntry?.isIntersecting || this.hasAnimated) {
          return;
        }

        this.hasAnimated = true;

        this.counters().forEach((counter) => {
          this.animateCounter(counter.nativeElement);
        });

        this.observer?.unobserve(this.statisticsSection().nativeElement);
      },
      {
        threshold: 0.4,
      },
    );

    this.observer.observe(this.statisticsSection().nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();

    this.animationFrameIds.forEach((animationFrameId) => {
      cancelAnimationFrame(animationFrameId);
    });

    this.animationFrameIds.clear();
  }

  private animateCounter(element: HTMLElement): void {
    const originalText = element.textContent?.trim() ?? '';
    const numberMatch = originalText.match(/\d[\d,.]*/);

    if (!numberMatch) {
      return;
    }

    const rawNumber = numberMatch[0];
    const targetValue = Number(rawNumber.replace(/[,\s]/g, ''));

    if (Number.isNaN(targetValue)) {
      return;
    }

    const prefix = originalText.slice(0, numberMatch.index);
    const suffix = originalText.slice((numberMatch.index ?? 0) + rawNumber.length);

    const duration = 1400;
    const startTime = performance.now();

    const formatNumber = (value: number): string => {
      return Math.round(value).toLocaleString('en-US');
    };

    const easeOutCubic = (value: number): number => {
      return 1 - Math.pow(1 - value, EASE_OUT_CUBIC_POWER);
    };

    const update = (currentTime: number): void => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const easedProgress = easeOutCubic(progress);

      const currentValue = targetValue * easedProgress;

      element.textContent = `${prefix}${formatNumber(currentValue)}${suffix}`;

      if (progress < 1) {
        const animationFrameId = requestAnimationFrame(update);
        this.animationFrameIds.set(element, animationFrameId);
      } else {
        element.textContent = originalText;
        this.animationFrameIds.delete(element);
      }
    };

    element.textContent = `${prefix}0${suffix}`;

    const animationFrameId = requestAnimationFrame(update);
    this.animationFrameIds.set(element, animationFrameId);
  }
}
